import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './dialedDigitExtractionServer.api';
import { getSelectedDialedDigitExtractionServer } from './dialedDigitExtractionServer.selectors';
import {
  GET_DIALED_DIGIT_EXTRACTION_SERVER_LIST,
  GET_DIALED_DIGIT_EXTRACTION_SERVER,
  CREATE_DIALED_DIGIT_EXTRACTION_SERVER,
  UPDATE_DIALED_DIGIT_EXTRACTION_SERVER,
  DELETE_DIALED_DIGIT_EXTRACTION_SERVER,
  GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_LIST,
  GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_EDIT,
  GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_VIEW,
  GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_CREATE,
  SELECT_DIALED_DIGIT_EXTRACTION_SERVER,
  DeleteDialedDigitExtractionServerThunkAction,
  UpdateDialedDigitExtractionServerThunkAction,
  CreateDialedDigitExtractionServerThunkAction,
  SET_DIALED_DIGIT_EXTRACTION_SERVER_LOADING,
  SetDialedDigitExtractionServerLoadingAction,
  DialedDigitExtractionServerActionTypes,
  DialedDigitExtractionServer,
  SelectDialedDigitExtractionServerAction,
} from './dialedDigitExtractionServer.types';
import { DIALED_DIGIT_EXTRACTION_SERVER_ROUTE, SYSTEM, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';

export function goToDialedDigitExtractionServerList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${DIALED_DIGIT_EXTRACTION_SERVER_ROUTE}`);
  };
}

export function goToDialedDigitExtractionServerCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${DIALED_DIGIT_EXTRACTION_SERVER_ROUTE}/create`);
  };
}

export function goToDialedDigitExtractionServerEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${DIALED_DIGIT_EXTRACTION_SERVER_ROUTE}/edit`);
  };
}

export function goToDialedDigitExtractionServerView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_DIALED_DIGIT_EXTRACTION_SERVER_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${DIALED_DIGIT_EXTRACTION_SERVER_ROUTE}/view`);
  };
}

export function setDialedDigitExtractionServerLoadingAction(
  isLoading: boolean,
): SetDialedDigitExtractionServerLoadingAction {
  return {
    type: SET_DIALED_DIGIT_EXTRACTION_SERVER_LOADING,
    payload: { isLoading },
  };
}

export function getDialedDigitExtractionServerList(deliveryFunctionId: string): DialedDigitExtractionServerActionTypes {
  return {
    type: GET_DIALED_DIGIT_EXTRACTION_SERVER_LIST,
    payload: api.getDialedDigitExtractionServerList(deliveryFunctionId),
  };
}

export function getDialedDigitExtractionServer(
  deliveryFunctionId: string, id: string,
): DialedDigitExtractionServerActionTypes {
  return {
    type: GET_DIALED_DIGIT_EXTRACTION_SERVER,
    payload: api.getDialedDigitExtractionServer(deliveryFunctionId, id),
  };
}

export function createDialedDigitExtractionServer(
  deliveryFunctionId: string,
  body: WithAuditDetails<DialedDigitExtractionServer>,
  history: History,
): CreateDialedDigitExtractionServerThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_DIALED_DIGIT_EXTRACTION_SERVER,
        payload: api.createDialedDigitExtractionServer(deliveryFunctionId, body),
      });

      dispatch(getDialedDigitExtractionServerList(deliveryFunctionId));
      dispatch(goToDialedDigitExtractionServerList(history));
      showSuccessMessage(i18n.t('dialedDigitExtractionServer.create.success', { msid: body.data?.msid }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('dialedDigitExtractionServer.create.failed', { msid: body.data?.msid, error: errorMessage }));
    }
  };
}

export function createDialedDigitExtractionServerFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<DialedDigitExtractionServer>,
): CreateDialedDigitExtractionServerThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setDialedDigitExtractionServerLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_DIALED_DIGIT_EXTRACTION_SERVER,
        payload: api.createDialedDigitExtractionServer(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('dialedDigitExtractionServer.create.success', { msid: body.data?.msid }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('dialedDigitExtractionServer.create.failed', { msid: body.data?.msid, error: errorMessage }));

    } finally {
      dispatch(setDialedDigitExtractionServerLoadingAction(false));
      dispatch(getDialedDigitExtractionServerList(deliveryFunctionId));
    }
  };
}

export function updateDialedDigitExtractionServer(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<DialedDigitExtractionServer>,
  history: History,
): UpdateDialedDigitExtractionServerThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_DIALED_DIGIT_EXTRACTION_SERVER,
        payload: api.updateDialedDigitExtractionServer(deliveryFunctionId, id, body),
      });

      dispatch(getDialedDigitExtractionServerList(deliveryFunctionId));
      dispatch(goToDialedDigitExtractionServerList(history));
      showSuccessMessage(i18n.t('dialedDigitExtractionServer.update.success', { msid: body.data?.msid }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('dialedDigitExtractionServer.update.failed', { msid: body.data?.msid, error: errorMessage }));
    }
  };
}

export function deleteDialedDigitExtractionServer(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteDialedDigitExtractionServerThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedDialedDigitExtractionServer] = getSelectedDialedDigitExtractionServer(getState());

    try {
      await dispatch({
        type: DELETE_DIALED_DIGIT_EXTRACTION_SERVER,
        payload: api.deleteDialedDigitExtractionServer(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('dialedDigitExtractionServer.delete.success', { msid: selectedDialedDigitExtractionServer?.msid }), 5000);
      if (history != null) {
        dispatch(goToDialedDigitExtractionServerList(history));
      }
    } catch (e) {
      showError(i18n.t('dialedDigitExtractionServer.delete.failed', { msid: selectedDialedDigitExtractionServer?.msid }));

    } finally {
      dispatch(getDialedDigitExtractionServerList(deliveryFunctionId));
    }
  };
}

export function selectDialedDigitExtractionServer(
  dialedDigitExtractionServer: DialedDigitExtractionServer[],
): SelectDialedDigitExtractionServerAction {
  return {
    type: SELECT_DIALED_DIGIT_EXTRACTION_SERVER,
    payload: dialedDigitExtractionServer,
  };
}
